import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, TemplateRef, ViewChild } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { CdkMenuTrigger, CdkMenu, CdkMenuItem } from '@angular/cdk/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { defaultRoomListColumns, dropDownMenuPosition } from '../../consts';
import { RoomListComponent } from '../room-list/room-list.component';
import { SectionComponent } from '../../shared';
import { ComponentsModule, Column, ConfirmService } from '../../../../lib';
import { RoomEditorComponent } from '../room-editor/room-editor.component';
import { AddRoomsComponent } from '../add-rooms/add-rooms.component';
import { SetRoomsEvent, Level, MeasurementUnit } from '../../models';
import { CRYPTO } from '../../tokens';
import { NumberPipe, TranslateRoomPipe, LevelNamePipe, ValueWithUnitPipe } from '../../pipes';

@Component({
    selector: 'ca-rbr-level-rooms-panel',
    templateUrl: './level-rooms-panel.component.html',
    styleUrl: './level-rooms-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SectionComponent,
        ComponentsModule,
        MatIconButton,
        CdkMenuTrigger,
        RoomListComponent,
        RoomEditorComponent,
        CdkMenu,
        CdkMenuItem,
        MatTooltip,
        TranslatePipe,
        ValueWithUnitPipe,
        TranslateRoomPipe,
        LevelNamePipe,
        AddRoomsComponent,
        NumberPipe
    ]
})
export class LevelRoomsPanelComponent implements OnInit {
    public levels = input.required<Level[]>();
    public level = input.required<Level>();
    public openAddRooms = input(false);
    public showValidationErrors = input(false);
    public setRooms = output<SetRoomsEvent>();
    public deleteLevel = output<void>();
    public toggleCooling = output<{ roomId: string; cooling: boolean }>();
    public toggleHeating = output<{ roomId: string; heating: boolean }>();
    public deleteRoom = output<string>();
    public columns: Column[] = [];
    public readonly measurementUnit = MeasurementUnit;
    public readonly dropDownMenuPosition = dropDownMenuPosition;
    public deleteLevelUUID: string;

    public readonly hasRoomWithMissingArea = computed(() => this.level().roomsData.some(room => room.area == null));

    private confirmService = inject(ConfirmService);
    private _crypto = inject(CRYPTO);

    @ViewChild('roomNameCellEl', { static: true, read: TemplateRef }) public roomNameCellEl!: TemplateRef<any>;
    @ViewChild('roomAreaCellEl', { static: true, read: TemplateRef }) public roomAreaCellEl!: TemplateRef<any>;
    @ViewChild('roomHeightCellEl', { static: true, read: TemplateRef }) public roomHeightCellEl!: TemplateRef<any>;
    @ViewChild('roomTypeCellEl', { static: true, read: TemplateRef }) public roomTypeCellEl!: TemplateRef<any>;
    @ViewChild('roomIsCooledCellEl', { static: true, read: TemplateRef }) public roomIsCooledCellEl!: TemplateRef<any>;
    @ViewChild('roomCoolingLoadCellEl', { static: true, read: TemplateRef }) public roomCoolingLoadCellEl!: TemplateRef<any>;
    @ViewChild('roomIsHeatedCellEl', { static: true, read: TemplateRef }) public roomIsHeatedCellEl!: TemplateRef<any>;
    @ViewChild('roomHeatingLoadCellEl', { static: true, read: TemplateRef }) public roomHeatingLoadCellEl!: TemplateRef<any>;
    @ViewChild('roomActionsCellEl', { static: true, read: TemplateRef }) public roomActionsCellEl!: TemplateRef<any>;

    async ngOnInit() {
        this.deleteLevelUUID = this._crypto.randomUUID();
        this.initColumns();
    }

    private initColumns(): void {
        this.columns = defaultRoomListColumns.map(column => {
            switch (column.name) {
                case 'name':
                    return { ...column, templateRef: this.roomNameCellEl };
                case 'area':
                    return { ...column, templateRef: this.roomAreaCellEl };
                case 'height':
                    return { ...column, templateRef: this.roomHeightCellEl };
                case 'type':
                    return { ...column, templateRef: this.roomTypeCellEl };
                case 'isCooled':
                    return { ...column, templateRef: this.roomIsCooledCellEl };
                case 'coolingLoad':
                    return { ...column, templateRef: this.roomCoolingLoadCellEl };
                case 'isHeated':
                    return { ...column, templateRef: this.roomIsHeatedCellEl };
                case 'heatingLoad':
                    return { ...column, templateRef: this.roomHeatingLoadCellEl };
                case 'actions':
                    return { ...column, templateRef: this.roomActionsCellEl };
                default:
                    break;
            }
            return column;
        });
    }

    public onDeleteLevel(): void {
        if (!this.level().roomsData.length) {
            this.deleteLevel.emit();
            return;
        }

        this.confirmService
            .getDialog(this.deleteLevelUUID)
            .pipe(filter(output => !!output?.confirm))
            .subscribe(() => this.deleteLevel.emit());
    }
}
